import React, { useState, useEffect, useMemo } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import Select from '../../components/ui/Select';
import ProspectStatusBadge from './components/ProspectStatusBadge';
import ProspectFormModal from './components/ProspectFormModal';
import ProspectDetailPanel from './components/ProspectDetailPanel';
import ConvertToCustomerModal from './components/ConvertToCustomerModal';
import { prospectService } from '../../services/prospectService';
import { useNavigate } from 'react-router-dom';

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'new', label: 'New Lead' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'proposal_sent', label: 'Proposal Sent' },
  { value: 'negotiating', label: 'Negotiating' },
  { value: 'won', label: 'Converted' },
  { value: 'lost', label: 'Lost' },
  { value: 'on_hold', label: 'On Hold' },
];

const COMPANY_TYPE_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'residential', label: 'Residential' },
  { value: 'industrial', label: 'Industrial' },
  { value: 'government', label: 'Government' },
  { value: 'nonprofit', label: 'Non-Profit' },
  { value: 'other', label: 'Other' },
];

const SOURCE_OPTIONS = [
  { value: 'all', label: 'All Sources' },
  { value: 'referral', label: 'Referral' },
  { value: 'website', label: 'Website' },
  { value: 'cold_call', label: 'Cold Call' },
  { value: 'trade_show', label: 'Trade Show' },
  { value: 'linkedin', label: 'Social Media' },
  { value: 'email_campaign', label: 'Email Campaign' },
  { value: 'advertisement', label: 'Advertisement' },
  { value: 'other', label: 'Other' },
];

const PIPELINE_STAGES = [
  { key: 'new', label: 'New Lead', color: 'bg-blue-500' },
  { key: 'contacted', label: 'Contacted', color: 'bg-purple-500' },
  { key: 'qualified', label: 'Qualified', color: 'bg-amber-500' },
  { key: 'proposal_sent', label: 'Proposal Sent', color: 'bg-orange-500' },
  { key: 'negotiating', label: 'Negotiating', color: 'bg-indigo-500' },
  { key: 'won', label: 'Converted', color: 'bg-green-500' },
  { key: 'lost', label: 'Lost', color: 'bg-red-500' },
];

const ProspectManagement = () => {
  const navigate = useNavigate();
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [sortKey, setSortKey] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingProspect, setEditingProspect] = useState(null);
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [convertingProspect, setConvertingProspect] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => { fetchProspects(); }, []);

  const fetchProspects = async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await prospectService?.getAll();
    if (err) setError('Failed to load prospects. Please try again.');
    else setProspects(data || []);
    setLoading(false);
  };

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const filteredProspects = useMemo(() => {
    let list = [...prospects];
    if (search) list = list?.filter(p => p?.companyName?.toLowerCase()?.includes(search?.toLowerCase()));
    if (statusFilter !== 'all') list = list?.filter(p => p?.status === statusFilter);
    if (typeFilter !== 'all') list = list?.filter(p => p?.companyType === typeFilter);
    if (sourceFilter !== 'all') list = list?.filter(p => p?.source === sourceFilter);
    list?.sort((a, b) => {
      let av = a?.[sortKey] ?? '';
      let bv = b?.[sortKey] ?? '';
      if (typeof av === 'string') av = av?.toLowerCase();
      if (typeof bv === 'string') bv = bv?.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [prospects, search, statusFilter, typeFilter, sourceFilter, sortKey, sortDir]);

  const pipelineSummary = useMemo(() => {
    return PIPELINE_STAGES?.map(stage => ({
      ...stage,
      count: prospects?.filter(p => p?.status === stage?.key)?.length,
      value: prospects?.filter(p => p?.status === stage?.key)?.reduce((sum, p) => sum + (Number(p?.estimatedValue) || 0), 0)
    }));
  }, [prospects]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const handleSaveProspect = async (formData) => {
    const prospectPayload = {
      companyName: formData?.companyName,
      companyType: formData?.companyType,
      status: formData?.status,
      source: formData?.source,
      website: formData?.website || null,
      address: formData?.address || null,
      city: formData?.city || null,
      state: formData?.state || null,
      zipCode: formData?.zipCode || null,
      estimatedValue: formData?.estimatedValue ? parseFloat(formData?.estimatedValue) : null,
      probability: formData?.probability ? parseInt(formData?.probability) : 0,
      notes: formData?.notes || null,
    };

    let savedProspect;
    if (editingProspect) {
      const { data, error: err } = await prospectService?.update(editingProspect?.id, prospectPayload);
      if (err) { alert('Failed to update prospect'); return; }
      savedProspect = data;
    } else {
      const { data, error: err } = await prospectService?.create(prospectPayload);
      if (err) { alert('Failed to create prospect'); return; }
      savedProspect = data;
      // Create primary contact if provided
      if (formData?.contactFirstName && savedProspect?.id) {
        await prospectService?.createContact({
          prospectId: savedProspect?.id,
          firstName: formData?.contactFirstName,
          lastName: formData?.contactLastName,
          title: formData?.contactTitle || null,
          email: formData?.contactEmail || null,
          phone: formData?.contactPhone || null,
          isPrimary: true
        });
      }
    }

    setFormModalOpen(false);
    setEditingProspect(null);
    showSuccess(editingProspect ? 'Prospect updated successfully' : 'Prospect added successfully');
    fetchProspects();
  };

  const handleConvert = async (customerData) => {
    if (!convertingProspect) return;
    const { error: err } = await prospectService?.convertToCustomer(convertingProspect?.id, customerData);
    if (err) { alert('Failed to convert prospect'); return; }
    setConvertModalOpen(false);
    setConvertingProspect(null);
    if (selectedProspect?.id === convertingProspect?.id) setSelectedProspect(null);
    showSuccess('Prospect successfully converted to customer!');
    fetchProspects();
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    const { error: err } = await prospectService?.delete(deleteConfirm?.id);
    if (!err) {
      if (selectedProspect?.id === deleteConfirm?.id) setSelectedProspect(null);
      showSuccess('Prospect deleted');
      fetchProspects();
    }
    setDeleteConfirm(null);
    setDeleting(false);
  };

  const SortIcon = ({ col }) => (
    <Icon name={sortKey === col ? (sortDir === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={14} className="ml-1 inline" />
  );

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Prospects</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Manage your sales pipeline and track potential customers</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" iconName="BarChart3" iconPosition="left" onClick={() => navigate('/prospect-analytics-dashboard')}>
              Analytics
            </Button>
            <Button iconName="Plus" iconPosition="left" onClick={() => { setEditingProspect(null); setFormModalOpen(true); }}>
              Add Prospect
            </Button>
          </div>
        </div>

        {/* Success message */}
        {successMsg && (
          <div className="bg-green-50 border border-green-200 text-green-800 rounded-md px-4 py-3 flex items-center gap-2">
            <Icon name="CheckCircle" size={16} />
            {successMsg}
          </div>
        )}

        {/* Pipeline Summary */}
        <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
          {pipelineSummary?.map(stage => (
            <button key={stage?.key}
              onClick={() => setStatusFilter(statusFilter === stage?.key ? 'all' : stage?.key)}
              className={`rounded-lg p-3 text-left border transition-all ${
                statusFilter === stage?.key ? 'border-accent bg-accent/5' : 'border-border bg-card hover:border-accent/50'
              }`}>
              <div className={`w-2 h-2 rounded-full ${stage?.color} mb-2`} />
              <p className="text-xs text-muted-foreground">{stage?.label}</p>
              <p className="text-lg font-bold text-foreground">{stage?.count}</p>
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="relative">
              <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                className="h-10 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                placeholder="Search prospects..."
                value={search}
                onChange={e => setSearch(e?.target?.value)}
              />
            </div>
            <Select options={STATUS_FILTER_OPTIONS} value={statusFilter} onChange={setStatusFilter} />
            <Select options={COMPANY_TYPE_OPTIONS} value={typeFilter} onChange={setTypeFilter} />
            <Select options={SOURCE_OPTIONS} value={sourceFilter} onChange={setSourceFilter} />
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-4 py-3 flex items-center gap-2">
            <Icon name="AlertCircle" size={16} />
            {error}
            <Button size="sm" variant="outline" className="ml-auto" onClick={fetchProspects}>Retry</Button>
          </div>
        )}

        {/* Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent" />
            </div>
          ) : filteredProspects?.length === 0 ? (
            <div className="text-center py-16">
              <Icon name="Target" size={40} className="mx-auto text-muted-foreground/40 mb-3" />
              <p className="text-muted-foreground">No prospects found</p>
              <Button className="mt-4" iconName="Plus" iconPosition="left" onClick={() => { setEditingProspect(null); setFormModalOpen(true); }}>Add First Prospect</Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/30 border-b border-border">
                  <tr>
                    {[
                      { key: 'companyName', label: 'Company' },
                      { key: 'companyType', label: 'Type' },
                      { key: 'status', label: 'Status' },
                      { key: 'source', label: 'Source' },
                      { key: 'estimatedValue', label: 'Est. Value' },
                      { key: 'probability', label: 'Probability' },
                      { key: 'leadScore', label: 'Score' },
                    ]?.map(col => (
                      <th key={col?.key}
                        className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                        onClick={() => handleSort(col?.key)}>
                        {col?.label}<SortIcon col={col?.key} />
                      </th>
                    ))}
                    <th className="px-4 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredProspects?.map(prospect => {
                    const primaryContact = prospect?.prospectContacts?.find(c => c?.isPrimary) || prospect?.prospectContacts?.[0];
                    const overdueFollowups = prospect?.prospectFollowups?.filter(f => f?.followupStatus === 'overdue')?.length || 0;
                    return (
                      <tr key={prospect?.id}
                        className={`hover:bg-muted/20 cursor-pointer transition-colors ${
                          selectedProspect?.id === prospect?.id ? 'bg-accent/5' : ''
                        }`}
                        onClick={() => setSelectedProspect(prospect)}>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm font-medium text-foreground">{prospect?.companyName}</p>
                            {primaryContact && (
                              <p className="text-xs text-muted-foreground">{primaryContact?.firstName} {primaryContact?.lastName}</p>
                            )}
                            {overdueFollowups > 0 && (
                              <span className="text-xs text-red-600 flex items-center gap-1 mt-0.5">
                                <Icon name="AlertCircle" size={11} /> {overdueFollowups} overdue
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-muted-foreground capitalize">{prospect?.companyType?.replace(/_/g, ' ')}</span>
                        </td>
                        <td className="px-4 py-3">
                          <ProspectStatusBadge status={prospect?.status} />
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-muted-foreground capitalize">{prospect?.source?.replace(/_/g, ' ')}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-foreground">
                            {prospect?.estimatedValue ? `$${Number(prospect?.estimatedValue)?.toLocaleString()}` : '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {prospect?.probability != null ? (
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                                <div className="h-full bg-accent rounded-full" style={{ width: `${prospect?.probability}%` }} />
                              </div>
                              <span className="text-xs text-muted-foreground">{prospect?.probability}%</span>
                            </div>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-sm font-semibold ${
                            (prospect?.leadScore || 0) >= 70 ? 'text-green-600' :
                            (prospect?.leadScore || 0) >= 40 ? 'text-amber-600' : 'text-muted-foreground'
                          }`}>{prospect?.leadScore ?? 0}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1" onClick={e => e?.stopPropagation()}>
                            <button
                              className="p-1.5 text-muted-foreground hover:text-foreground rounded"
                              onClick={() => { setEditingProspect(prospect); setFormModalOpen(true); }}
                              title="Edit">
                              <Icon name="Edit" size={15} />
                            </button>
                            {prospect?.status !== 'won' && (
                              <button
                                className="p-1.5 text-muted-foreground hover:text-green-600 rounded"
                                onClick={() => { setConvertingProspect(prospect); setConvertModalOpen(true); }}
                                title="Convert to Customer">
                                <Icon name="UserCheck" size={15} />
                              </button>
                            )}
                            <button
                              className="p-1.5 text-muted-foreground hover:text-destructive rounded"
                              onClick={() => setDeleteConfirm(prospect)}
                              title="Delete">
                              <Icon name="Trash2" size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!loading && filteredProspects?.length > 0 && (
            <div className="px-4 py-3 border-t border-border text-xs text-muted-foreground">
              Showing {filteredProspects?.length} of {prospects?.length} prospects
            </div>
          )}
        </div>
      </div>
      {/* Detail Panel */}
      {selectedProspect && (
        <ProspectDetailPanel
          prospect={selectedProspect}
          onClose={() => setSelectedProspect(null)}
          onEdit={(p) => { setEditingProspect(p); setFormModalOpen(true); }}
          onConvert={(p) => { setConvertingProspect(p); setConvertModalOpen(true); }}
          onRefresh={fetchProspects}
        />
      )}
      {/* Form Modal */}
      <ProspectFormModal
        isOpen={formModalOpen}
        onClose={() => { setFormModalOpen(false); setEditingProspect(null); }}
        onSave={handleSaveProspect}
        prospect={editingProspect}
      />
      {/* Convert Modal */}
      <ConvertToCustomerModal
        isOpen={convertModalOpen}
        onClose={() => { setConvertModalOpen(false); setConvertingProspect(null); }}
        onConvert={handleConvert}
        prospect={convertingProspect}
      />
      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-card rounded-lg shadow-xl p-6 max-w-sm mx-4 w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Icon name="Trash2" size={20} className="text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Delete Prospect</h3>
                <p className="text-sm text-muted-foreground">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              Are you sure you want to delete <strong>{deleteConfirm?.companyName}</strong>? All contacts, interactions, and follow-ups will be permanently removed.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
              <Button variant="danger" loading={deleting} onClick={handleDelete}>Delete</Button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default ProspectManagement;
